import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Navigation, MapPin, Clock, Package, AlertTriangle, Boxes,
  Plus, ExternalLink, Image as ImageIcon, Upload, Truck, CheckCircle2, FileCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_ORDER = ['generated', 'picked_up', 'in_transit', 'in_storage', 'at_facility', 'disposed', 'certified'];
const STAGE_PROGRESS = { generated: 10, picked_up: 25, in_transit: 40, in_storage: 55, at_facility: 70, disposed: 90, certified: 100 };
const STAGE_LABEL = { generated: 'Generated', picked_up: 'Picked Up', in_transit: 'In Transit', in_storage: 'In Storage', at_facility: 'At Facility', disposed: 'Disposed', certified: 'Certified' };
const STAGE_ICON = { generated: Package, picked_up: Truck, in_transit: MapPin, in_storage: Boxes, at_facility: AlertTriangle, disposed: CheckCircle2, certified: FileCheck };

function AuthImage({ path, token, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false; let objUrl = null;
    axios.get(`${API}/recycling/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then((r) => { if (!revoked) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } })
      .catch(() => {});
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path, token]);
  if (!url) return <div className="w-full h-24 bg-gray-100 rounded flex items-center justify-center text-gray-300"><ImageIcon className="w-6 h-6" /></div>;
  return <img src={url} alt={alt} className="w-full h-24 object-cover rounded border border-gray-200" data-testid="timeline-photo" />;
}

export default function RecyclingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const isGuest = user?.role === 'guest';
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const fileRef = useRef(null);

  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchLoad = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/recycling/loads/${id}`, config);
      setLoad(r.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      else if (err.response?.status === 404) { toast.error('Waste load not found'); navigate('/recycling'); }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => { fetchLoad(); }, [fetchLoad]);

  const advance = async (toStage) => {
    try {
      const r = await axios.patch(`${API}/recycling/loads/${id}/status${toStage ? `?target=${toStage}` : ''}`, {}, config);
      setLoad(r.data); setTarget('');
      toast.success(`Status updated to ${STAGE_LABEL[r.data.status]}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/recycling/loads/${id}/photos`, fd, { headers: { ...config.headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Compliance photo uploaded');
      await fetchLoad();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (loading) return <div className="flex items-center justify-center h-screen text-gray-400">Loading…</div>;
  if (!load) return null;

  const progress = STAGE_PROGRESS[load.status] || 0;
  const gps = load.gps_history?.[load.gps_history.length - 1];
  const lat = gps?.lat ?? load.origin_lat;
  const lon = gps?.lon ?? load.origin_lon;
  const d = 0.04;
  const bbox = `${lon - d},${lat - d},${lon + d},${lat + d}`;
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
  const osmLink = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=12/${lat}/${lon}`;
  const remaining = STAGE_ORDER.slice(STAGE_ORDER.indexOf(load.status) + 1);
  const isFinal = load.status === 'certified';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/recycling')} data-testid="back-to-recycling"><ArrowLeft className="w-4 h-4 mr-1" />Back to Dashboard</Button>
        <div className="h-5 w-px bg-gray-200" />
        <h1 className="text-xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>Manifest #{load.manifest_no}</h1>
      </header>

      <div className="max-w-6xl mx-auto p-6 space-y-5">
        {/* Status */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div><h2 className="text-lg font-bold text-black">Waste Load Status</h2><p className="text-sm text-gray-500">Current status and tracking progress</p></div>
              <span className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{STAGE_LABEL[load.status]}</span>
            </div>
            <div className="flex items-center justify-between text-sm mb-1"><span className="text-gray-600">Progress</span><span className="font-semibold">{progress}%</span></div>
            <Progress value={progress} className="h-2.5" data-testid="progress-bar" />
            {!isGuest && !isFinal && (
              <div className="flex items-center gap-2 mt-4 justify-end flex-wrap">
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="w-44" data-testid="status-target-select"><SelectValue placeholder="Jump to stage…" /></SelectTrigger>
                  <SelectContent>{remaining.map((s) => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={() => advance(target || null)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="update-status-btn">
                  <Plus className="w-4 h-4 mr-1" />Update Status
                </Button>
              </div>
            )}
            {isFinal && <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2"><FileCheck className="w-4 h-4" />Chain of custody closed — destruction/recovery certified.</div>}
          </CardContent>
        </Card>

        {/* GPS */}
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Navigation className="w-4 h-4 text-blue-600" />Live GPS Tracking</CardTitle>
            <p className="text-sm text-gray-500">Real-time location monitoring · Tracker {load.tracker_id} ({load.tracker_provider})</p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between bg-blue-50/50 border border-blue-100 rounded-lg px-4 py-3 mb-3 flex-wrap gap-2">
              <div className="text-sm">
                <div className="flex items-center gap-2 font-medium text-black"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Status: {STAGE_LABEL[load.status]}</div>
                <div className="text-gray-600 mt-1"><b>Last Known Location:</b> {load.last_location}</div>
                <div className="text-gray-600"><b>Last Update:</b> {new Date(load.updated_at).toLocaleString()}</div>
              </div>
              <a href={osmLink} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="view-on-map"><MapPin className="w-4 h-4 mr-1" />View on Map<ExternalLink className="w-3 h-3 ml-1" /></Button>
              </a>
            </div>
            <iframe title="gps-map" src={mapSrc} className="w-full h-72 rounded-lg border border-gray-200" data-testid="gps-map" />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Info */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-gray-600" />Waste Load Information</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <div className="font-semibold text-black mb-1">Generator Details</div>
                <Row k="Name" v={load.generator_name} /><Row k="Address" v={load.generator_address} /><Row k="EPA ID" v={load.epa_id} />
              </div>
              <div>
                <div className="font-semibold text-black mb-1">Waste Details</div>
                <Row k="Type" v={load.waste_type} /><Row k="Description" v={load.description} /><Row k="Quantity" v={`${load.quantity} ${load.quantity_unit}`} /><Row k="Hazard Class" v={load.hazard_class} />
              </div>
              <div>
                <div className="font-semibold text-black mb-1">Packaging &amp; Destination</div>
                <Row k="Container" v={load.container_type} /><Row k="Facility" v={load.facility_name} /><Row k="Facility Address" v={load.facility_address} />
              </div>
              {load.safety_notes && <div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 text-yellow-800"><b>Safety Notes:</b> {load.safety_notes}</div>}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-gray-600" />Tracking Timeline</CardTitle>
              {!isGuest && (
                <>
                  <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={onUpload} data-testid="photo-input" />
                  <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn">
                    <Upload className="w-4 h-4 mr-1" />{uploading ? 'Uploading…' : 'Add Photo'}
                  </Button>
                </>
              )}
            </CardHeader>
            <CardContent className="space-y-4 max-h-[520px] overflow-y-auto">
              {(load.timeline || []).map((ev, i) => {
                const Icon = STAGE_ICON[ev.stage] || Package;
                return (
                  <div key={`${ev.stage}-${ev.timestamp}-${i}`} className="flex gap-3" data-testid={`timeline-event-${ev.stage}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-emerald-600" /></div>
                      {i < load.timeline.length - 1 && <div className="w-px flex-1 bg-gray-200 my-1" />}
                    </div>
                    <div className="flex-1 pb-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">{ev.stage_label}</span>
                      <p className="text-sm text-gray-800 mt-1">{ev.note}</p>
                      <p className="text-xs text-gray-400">{new Date(ev.timestamp).toLocaleString()} · {ev.location_label}</p>
                      {ev.photos?.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 flex items-center gap-1 mb-1"><ImageIcon className="w-3 h-3" />Compliance Documentation ({ev.photos.length})</div>
                          <div className="grid grid-cols-2 gap-2">
                            {ev.photos.map((p) => <AuthImage key={p.id} path={p.storage_path} token={token} alt={p.filename} />)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-0.5"><span className="text-gray-500">{k}:</span><span className="text-gray-800 text-right">{v}</span></div>
);
